import type { TouchPointCreateDto, TouchPointUpdateDto } from '@/types/touchPoint'
import type { TouchPointFormData } from '@/components/webs/modals/TouchPointModal'
import { normalizeMeetingUrl } from '@/utils/meetingUrl'

// Email touchpoints are built and saved directly inside EmailActivationPanel
// (not through this mapper), so the email case is intentionally omitted from
// the switch below.

/**
 * Build a create DTO for any touchpoint type
 */
export function buildTouchPointCreateDto(
  formData: TouchPointFormData,
  campaignId: string,
  webId?: string
): TouchPointCreateDto {
  switch (formData.Type) {
    case 'Meeting':
      return buildMeetingCreateDto(formData, campaignId, webId)
    case 'Phone':
      return buildPhoneCreateDto(formData, campaignId, webId)
    case 'Social':
      return buildSocialCreateDto(formData, campaignId, webId)
    default:
      throw new Error(`Unsupported touchpoint type: ${formData.Type}`)
  }
}

/**
 * Build an update DTO for any touchpoint type
 */
export function buildTouchPointUpdateDto(
  formData: TouchPointFormData,
  existingETag: string
): TouchPointUpdateDto {
  switch (formData.Type) {
    case 'Meeting':
      return buildMeetingUpdateDto(formData, existingETag)
    case 'Phone':
      return buildPhoneUpdateDto(formData, existingETag)
    case 'Social':
      return buildSocialUpdateDto(formData, existingETag)
    default:
      throw new Error(`Unsupported touchpoint type: ${formData.Type}`)
  }
}

/**
 * Build a create DTO for a Meeting touchpoint
 */
export function buildMeetingCreateDto(
  formData: TouchPointFormData,
  campaignId: string,
  webId?: string
): TouchPointCreateDto {
  return {
    CampaignId: campaignId,
    WebId: webId,
    Name: formData.Name,
    Type: formData.Type,
    MeetingProperties:
      formData.Type === 'Meeting' && formData.MeetingType && formData.TimeZone && formData.StartTime && formData.EndTime
        ? {
            Type: formData.MeetingType,
            TimeZone: formData.TimeZone,
            StartTime: formData.StartTime,
            EndTime: formData.EndTime,
            MeetingUrlLink: normalizeMeetingUrl(formData.MeetingUrlLink) || undefined,
            SendWebster: formData.SendWebster,
          }
        : undefined,
  }
}

/**
 * Build an update DTO for a Meeting touchpoint
 */
export function buildMeetingUpdateDto(formData: TouchPointFormData, existingETag: string): TouchPointUpdateDto {
  return {
    Name: formData.Name,
    ETag: formData.ETag || existingETag,
    MeetingProperties:
      formData.Type === 'Meeting' && formData.MeetingType && formData.TimeZone && formData.StartTime && formData.EndTime
        ? {
            Type: formData.MeetingType,
            TimeZone: formData.TimeZone,
            StartTime: formData.StartTime,
            EndTime: formData.EndTime,
            MeetingUrlLink: normalizeMeetingUrl(formData.MeetingUrlLink) || undefined,
            SendWebster: formData.SendWebster,
          }
        : undefined,
  }
}

/**
 * Build a create DTO for a Phone touchpoint
 */
export function buildPhoneCreateDto(
  formData: TouchPointFormData,
  campaignId: string,
  webId?: string
): TouchPointCreateDto {
  // Convert date string (YYYY-MM-DD) to UTC midnight ISO string
  const scheduledDate = new Date(formData.ScheduledDate + 'T00:00:00.000Z')

  return {
    CampaignId: campaignId,
    WebId: webId,
    Name: formData.Name,
    Type: formData.Type,
    PhoneProperties:
      formData.Type === 'Phone' && formData.NumberOfDials !== undefined
        ? {
            ScheduledDate: scheduledDate.toISOString(),
            NumberOfDials: formData.NumberOfDials,
          }
        : undefined,
  }
}

/**
 * Build an update DTO for a Phone touchpoint
 */
export function buildPhoneUpdateDto(formData: TouchPointFormData, existingETag: string): TouchPointUpdateDto {
  // Convert date string (YYYY-MM-DD) to UTC midnight ISO string
  const scheduledDate = new Date(formData.ScheduledDate + 'T00:00:00.000Z')

  return {
    Name: formData.Name,
    ETag: formData.ETag || existingETag,
    PhoneProperties:
      formData.Type === 'Phone' && formData.NumberOfDials !== undefined
        ? {
            ScheduledDate: scheduledDate.toISOString(),
            NumberOfDials: formData.NumberOfDials,
          }
        : undefined,
  }
}

/**
 * Build a create DTO for a Social touchpoint
 */
export function buildSocialCreateDto(
  formData: TouchPointFormData,
  campaignId: string,
  webId?: string
): TouchPointCreateDto {
  // Convert date string (YYYY-MM-DD) to UTC midnight ISO string
  const scheduledDate = new Date(formData.ScheduledDate + 'T00:00:00.000Z')

  return {
    CampaignId: campaignId,
    WebId: webId,
    Name: formData.Name,
    Type: formData.Type,
    SocialProperties:
      formData.Type === 'Social' && formData.PostCopy && formData.Platform
        ? {
            ScheduledDate: scheduledDate.toISOString(),
            PostCopy: formData.PostCopy,
            Platform: formData.Platform,
          }
        : undefined,
  }
}

/**
 * Build an update DTO for a Social touchpoint
 */
export function buildSocialUpdateDto(formData: TouchPointFormData, existingETag: string): TouchPointUpdateDto {
  // Convert date string (YYYY-MM-DD) to UTC midnight ISO string
  const scheduledDate = new Date(formData.ScheduledDate + 'T00:00:00.000Z')

  return {
    Name: formData.Name,
    ETag: formData.ETag || existingETag,
    SocialProperties:
      formData.Type === 'Social' && formData.PostCopy && formData.Platform
        ? {
            ScheduledDate: scheduledDate.toISOString(),
            PostCopy: formData.PostCopy,
            Platform: formData.Platform,
          }
        : undefined,
  }
}
